/**
 * Module dependencies.
 */

import { HorizontalSlider, Input, Toggle } from 'src/components/core';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { StepPageConfig } from 'src/types/advisor';
import {
  availableFiltersKeys,
  modelRangeSpecsMeasures
} from 'src/core/content-config/model-ranges';

import { media, units } from '@untile/react-components';
import { useAdvisor } from 'src/context/advisor/context';
import BaseStep from './base-step';
import ContactFormStep from './contact-form';
import compact from 'lodash/compact';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import size from 'lodash/size';
import styled from 'styled-components';
import uniq from 'lodash/uniq';

/**
 * `Props` type.
 */

type Props = StepPageConfig & {
  className?: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  height: 100%;
  position: relative;
`;

/**
 * `StyledToggle` styled component.
 */

const StyledToggle = styled(Toggle)`
  margin-bottom: ${units(6)};
`;

/**
 * `StyledHorizontalSlider` styled component.
 */

const StyledHorizontalSlider = styled(HorizontalSlider)`
  margin-bottom: ${units(6)};

  ${media.max('md')`
    margin: 0 ${units(1)} ${units(6)};
  `}
`;

/**
 * `StyledInput` styled component.
 */

const StyledInput = styled(Input)`
  margin-bottom: ${units(6)};
  width: ${units(18)};
`;

/**
 * `GasContentSliderStep` component.
 */

const GasContentSliderStep = (props: Props): ReactElement => {
  const { className, pageIndex, pageKey } = props;
  const {
    filteredModelRanges,
    getCheckedValue,
    initialModelRanges,
    isSuccessModelRanges,
    onGoToNext,
    onGoToPrevious,
    onSetFilter,
    refetch
  } = useAdvisor();

  const [showButane, setShowButane] = useState<boolean>(true);
  const { butaneValues, propaneValues } = useMemo(() => {
    return {
      butaneValues: orderBy(
        compact(
          uniq(map(initialModelRanges, ({ butane }) => parseFloat(butane)))
        )
      ),
      propaneValues: orderBy(
        compact(
          uniq(map(initialModelRanges, ({ propane }) => parseFloat(propane)))
        )
      )
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { max, min } = useMemo(() => {
    const values = showButane ? butaneValues : propaneValues;
    const firstValue = Math.floor(head(values));
    const lastValue = Math.ceil(last(values));

    if (size(values) < 2) {
      return {
        max: lastValue,
        min: 0
      };
    }

    return {
      max: lastValue,
      min: firstValue < 0 ? 0 : firstValue
    };
  }, [butaneValues, propaneValues, showButane]);

  const checkedValue = getCheckedValue(pageKey);
  const [value, setValue] = useState<number>(0);
  const handleChange = useCallback((newValue: number) => {
    setValue(newValue);
  }, []);

  const handleContinue = useCallback(() => {
    const filterKey = showButane
      ? availableFiltersKeys.butane
      : availableFiltersKeys.propane;

    const filterValue = {
      from: `${Math.floor(value * 0.9)}`,
      to: `${Math.ceil(value * 1.1)}`
    };

    onSetFilter(filterKey, JSON.stringify(filterValue));
    refetch();
    onGoToNext(`${value}`, pageKey, pageIndex);
  }, [onGoToNext, onSetFilter, pageIndex, pageKey, refetch, showButane, value]);

  useEffect(() => {
    if (!checkedValue) {
      handleChange(Math.floor((max + min) / 2));

      return;
    }

    handleChange(parseFloat(checkedValue));
  }, [checkedValue, handleChange, max, min]);

  if (isSuccessModelRanges && isEmpty(filteredModelRanges)) {
    return <ContactFormStep />;
  }

  return (
    <Wrapper className={className}>
      <BaseStep
        onContinue={handleContinue}
        onGoBack={() => onGoToPrevious(pageIndex)}
        {...props}
      >
        <StyledToggle
          onChange={setShowButane}
          toggled={showButane}
          toggledLabel={'Butane'}
          unToggledLabel={'Propane'}
        />

        <StyledHorizontalSlider
          max={max}
          min={min}
          onChange={handleChange}
          value={(value < min && min) || (value > max && max) || value}
        />

        <StyledInput
          name={`${pageKey}-input`}
          onChange={({ target }) => handleChange(target.value)}
          unit={modelRangeSpecsMeasures.butane}
          value={value}
        />
      </BaseStep>
    </Wrapper>
  );
};

/**
 * Export `GasContentSliderStep` component.
 */

export default GasContentSliderStep;
